import React, {useState, useEffect} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {TextField, InputLabel, createStyles, Theme as AugmentedTheme, Dialog, DialogActions, Button} from '@material-ui/core';
import { CustomInputMappingField } from '@tuqqi/common';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { useSvgDrawing } from 'react-hooks-svgdrawing'
import { ClearIcon } from './ClearIcon';
import { dataUrlToFile, svgToPng } from './helpers';
import * as filestack from 'filestack-js';

interface ISignature {
    policy?: string,
    signature?: string,
    field: CustomInputMappingField,
    value: string,
    onChangeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void,
    isRtl: boolean
}

const useStyles = makeStyles((theme: AugmentedTheme) =>  createStyles({
    title: {
        marginTop: 16,
        marginLeft: 16,
        fontFamily: 'Arial',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: 16,
        lineHeight: '24px',
        letterSpacing: 0.147638,
        color: '#0C0C0D',
    },
    body: {
        position: 'relative',
        margin: 16,
        height: 160
    } as CSSProperties,
    actions: {

    },
    paper: {
        width: 447,
        maxWidth: 447
    },
    draw: {
        width: 415,
        height: 160,
        background: '#FFFFFF',
        border: '1px solid #D7D7DB',
        borderRadius: 4,
        marginRight: 'auto', 
        marginLeft: 'auto'
    },
    clear: {
        top: 8,
        right: 8,
        position: 'absolute',
        cursor: 'pointer',
    } as CSSProperties,
    img: {
        marginTop: 12,
        boxSizing: 'border-box',
        width: 270,
        height: 104,
        background: '#FFFFFF',
        border: '1px solid #D7D7DB',
        borderRadius: 4,
    },
    noWrap: {
        display: 'contents'
    },
    hiddenReq: {
        height: 1,
        width: 1,
        marginRight: 'auto',
        marginLeft: '50%',
        display: 'flex',
        opacity: 0,
    },
    root: {
        zIndex: '3302!important' as any
    },
    field: {
        width: '100%',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '.2px',
        lineHeight: '20px',
        color: '#202124',

        "&::placeholder": {
            fontSize: '14px',
        },
    },
      //@ts-ignore
      inputLabel: theme.inputLabel,
}))

type GetSvg = () => string | null

interface PropsDraw {
    classNameDraw: string, 
    classNameClear: string, 
    classNameBody: string, 
    classNameActions: string, 
    show: boolean, 
    onCancel: () => void
    onSave: (getSvg: GetSvg) => void
    // download: (s: string | null) => void
}

const emptySvg1 = '<svg width="415" height="160"></svg>'
const emptySvg2 = '<svg width="416.9965515136719" height="161.9965362548828"></svg>'

const DrawSignature = ({classNameClear, show, classNameDraw, classNameBody, classNameActions, onCancel, onSave}: PropsDraw) => {
    const [renderRef, draw] = useSvgDrawing({
        penWidth: 3,
    })
    const onClear = () => {
        draw.clear()
    }
    return (
        <>
        <div className={classNameBody}>
            <ClearIcon onClick={onClear} className={classNameClear} />
            <div className={classNameDraw} ref={renderRef} />
        </div>
        <DialogActions className={classNameActions}>
        <Button style={{textTransform: 'capitalize'}} color='primary' variant='text' onClick={onCancel} disabled={false}>
            Cancel
        </Button>
        <Button style={{textTransform: 'capitalize'}} color='primary' variant='text' onClick={() => onSave(() => draw.getSvgXML())} disabled={false}>
            Save
        </Button>
    </DialogActions>
    </>
    )
}

const getUrl = (value: string, url: string, policy: string, signature: string) => {
    if(!value || !signature || !policy || !url) return ''
    return `${url}?policy=${policy}&signature=${signature}`
}

export default function SignatureField({ field, value, onChangeHandler, isRtl, policy, signature }: ISignature) {
    const classes = useStyles();
    const [showModal, setShowModal] = useState(false)
    const [filestackClientInit, setFilestackClientInit] = useState<any>(null);
    const [linkUrl, setLinkUrl] = useState('');
    const url = getUrl(value, linkUrl, policy ?? '', signature ?? '')

    const openModal = () => {                
        setShowModal(true)
    }
    const closeModal = () => {        
        setShowModal(false)
    }

    const afterSave = (val: any | string) => {
        if(!val || !Object.keys(val).length) {
            onChangeHandler?.({ currentTarget: {value: ''} } as React.ChangeEvent<HTMLInputElement>)
            setShowModal(false)
            return
        }

        //@ts-ignore
        onChangeHandler?.({ currentTarget: {value: val} })
        setShowModal(false)
    }

    const onSave = async (getSvg: GetSvg) => {
        try {           
            const svgString = getSvg()
            
            if(!svgString || svgString === emptySvg1 || svgString === emptySvg2) {
                afterSave("")
                setLinkUrl("")
                return
            }
            const png = await svgToPng(svgString ?? '', 0, '#fff')
            const file = await dataUrlToFile(png, 'signature-image-temp.png', 'image/png')
            filestackClientInit && filestackClientInit.multiupload([file])
            .then((res: any[]) => {
                if(!res.length) {
                    afterSave("")
                    setLinkUrl("")
                    return
                }
                afterSave(res[0] ?? '')
                setLinkUrl(res[0]?.url ?? '')
            })
            .catch((e: any) => {
                afterSave("")
                setLinkUrl("")
            })
        } catch (error) {
            afterSave("")
            setLinkUrl("")      
        }
    }

    useEffect(() => {        
        if (policy && signature) {
            const apikey = 'AG1Zm0R1vQ3yoXm9IzMcxz';
            const filestackClient = filestack.init(apikey, { security: { 'policy': policy, 'signature': signature } });
            //@ts-ignore
            filestackClient.options = { maxFiles: 10, storeTo: { location: 's3' } }
            setFilestackClientInit(filestackClient);
        }

    }, [policy, signature])

    const Req =  field.required && (!url || !value) ? <TextField className={classes.hiddenReq} required style={{}} /> : null


    const NoValue =      <div className={classes.noWrap}>
    {/* <TextField  
            style={{ 
                direction: isRtl ? 'rtl' : 'ltr',
                textAlign: isRtl ? 'right' : 'left' 
            }} 
        disabled
        value={value} 
        id={field.sourceField} 
        className={classes.field} 
        placeholder={field.fieldName} 
        onChange={onChangeHandler} 
        name={field.fieldName} 
        type='text' 
        // required={field.required}                    
        required={false}                    
        inputProps={{dir: isRtl ? 'rtl' : 'ltr'}}
    /> */}
    {Req}
    <Button variant="outlined" onClick={openModal}>{field.fieldName}</Button>
    </div>

    const Value =      <>
        <InputLabel
    style={{ 
        direction: isRtl ? 'rtl' : 'ltr',
        textAlign: isRtl ? 'right' : 'left' 
    }} 
    htmlFor={field.sourceField} 
    className={classes.inputLabel}
    >{field.fieldName}</InputLabel>
    <img className={classes.img} src={url} alt="signature" />
    </>

    const Dial = <Dialog open={showModal} classes={{paper: classes.paper, root: classes.root}} dir='auto'>
        <div className={classes.title} >{"Signature field"}</div>
        <DrawSignature classNameClear={classes.clear} onCancel={closeModal} onSave={onSave} classNameActions={classes.actions} classNameBody={classes.body} classNameDraw={classes.draw} show={true} />
    </Dialog>

    return (
        <>
        

            {!url || !value ? NoValue : Value}
        
        {Dial}
        </>
    )
}